import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Heart, Shield, Clock, Home, Camera } from 'lucide-react';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';
import LatestFromBlog from '../components/LatestFromBlog';
import CustomerReviews from '../components/CustomerReviews';

const HomePage = () => {
  return (
    <div className="home-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner - Below Discount */}
      <ReviewBanner />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Professional In-Home Cat Sitting & Home Guardians in Walsall</h1>
          <p className="hero-subtitle">Your cats stay comfortable in their own home while we care for them — and keep your home secure while you're away.</p>
          <Link to="/contact">
            <Button size="lg" className="cta-button">
              Book Your Free Meet & Greet
            </Button>
          </Link>

          <ul className="hero-trust-badges" data-testid="hero-trust-badges">
            <li><CheckCircle2 size={18} aria-hidden="true" /> <span>Fully Insured</span></li>
            <li><CheckCircle2 size={18} aria-hidden="true" /> <span>DBS-Checked</span></li>
            <li><CheckCircle2 size={18} aria-hidden="true" /> <span>Walsall-Based</span></li>
          </ul>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title">Why In-Home Cat Care is Better</h2>
          <p className="section-subtitle">Cheaper than a cattery and so much better for your cats</p>
          
          <div className="benefits-grid">
            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Home className="benefit-icon" />
                <h3>Familiar Surroundings</h3>
                <p>Your cats stay in the comfort of their own home, surrounded by their favourite spots and scents.</p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Heart className="benefit-icon" />
                <h3>Less Stress & Disruption</h3>
                <p>No traumatic car journeys or unfamiliar environments. Your cats remain calm and happy.</p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Clock className="benefit-icon" />
                <h3>Maintain Normal Routine</h3>
                <p>We follow your cats' regular feeding and play schedule, keeping their routine consistent.</p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Shield className="benefit-icon" />
                <h3>Personalised Care</h3>
                <p>One-on-one attention tailored to each cat's unique personality and needs.</p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Home className="benefit-icon" />
                <h3>Home Looks Lived In</h3>
                <p>We keep your home secure and occupied-looking while you're away, deterring potential intruders.</p>
              </CardContent>
            </Card>

            <Card className="benefit-card">
              <CardContent className="benefit-content">
                <Camera className="benefit-icon" />
                <h3>Daily Photo Updates</h3>
                <p>Receive photos and messages about your cats every day, giving you complete peace of mind.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="services-overview-section">
        <div className="container">
          <h2 className="section-title">What's Included in Every Visit</h2>
          
          <div className="services-two-column">
            <div className="services-image-grid">
              <img src="https://images.unsplash.com/photo-1769246794265-ebe54c03702c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjYXQlMjBwb3J0cmFpdCUyMGZhY2UlMjBsb29raW5nJTIwY2FtZXJhJTIwY3V0ZXxlbnwwfHx8fDE3NzMxODkwOTN8MA&ixlib=rb-4.1.0&q=85" alt="Fluffy white cat looking at camera" className="service-img" />
              <img src="https://images.unsplash.com/photo-1763577364690-21ea0fed0aa5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxjYXQlMjByZWxheGluZyUyMGhvbWUlMjBjb3p5JTIwaW5kb29yfGVufDB8fHx8MTc3MzE4ODIzNHww&ixlib=rb-4.1.0&q=85" alt="Cat relaxing in a sunbeam" className="service-img" />
            </div>

            <div className="services-list">
              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Feeding & Fresh Water</h4>
                  <p>Following your specific instructions and schedule</p>
                </div>
              </div>

              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Litter Tray Cleaning</h4>
                  <p>Keeping everything fresh and hygienic</p>
                </div>
              </div>

              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Playtime or Companionship</h4>
                  <p>Ensuring your cats get attention and enrichment</p>
                </div>
              </div>

              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Health Monitoring</h4>
                  <p>Checking for any changes in behaviour or wellbeing</p>
                </div>
              </div>

              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Medication Administration</h4>
                  <p>If required, given with care and attention</p>
                </div>
              </div>

              <div className="service-item">
                <CheckCircle2 className="check-icon" />
                <div>
                  <h4>Daily Photo & Message Updates</h4>
                  <p>So you know your cats are happy and well</p>
                </div>
              </div>
            </div>
          </div>

          <div className="cta-center">
            <Link to="/services">
              <Button size="lg" variant="outline" className="secondary-cta">
                View Full Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Home Guardians Highlight */}
      <section className="home-guardians-section">
        <div className="container">
          <div className="guardians-content">
            <div className="guardians-text">
              <h2 className="section-title">Home Guardians Service</h2>
              <p className="guardians-intro">
                While caring for your cats, we also keep your home secure and lived-in, giving you complete peace of mind.
              </p>
              
              <div className="guardians-list">
                <div className="guardian-item">
                  <CheckCircle2 className="check-icon" />
                  <span>Open and close curtains</span>
                </div>
                <div className="guardian-item">
                  <CheckCircle2 className="check-icon" />
                  <span>Turn lights on and off</span>
                </div>
                <div className="guardian-item">
                  <CheckCircle2 className="check-icon" />
                  <span>Take in post and parcels</span>
                </div>
                <div className="guardian-item">
                  <CheckCircle2 className="check-icon" />
                  <span>Put bins out</span>
                </div>
              </div>

              <Link to="/home-guardians">
                <Button variant="outline" className="secondary-cta">
                  Learn More About Home Guardians
                </Button>
              </Link>
            </div>

            <div className="guardians-image">
              <img src="https://images.unsplash.com/photo-1760214695210-7aef133fa9a5" alt="Peaceful cat at home" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="container">
          <h2 className="section-title">Why Choose Maid for Cats?</h2>
          
          <div className="trust-grid">
            <Card className="trust-card">
              <CardContent className="trust-content">
                <Shield className="trust-icon" />
                <h3>Fully Insured</h3>
                <p>Comprehensive insurance for your complete peace of mind</p>
              </CardContent>
            </Card>

            <Card className="trust-card">
              <CardContent className="trust-content">
                <CheckCircle2 className="trust-icon" />
                <h3>DBS Checked</h3>
                <p>Background checked for your security and trust</p>
              </CardContent>
            </Card>

            <Card className="trust-card">
              <CardContent className="trust-content">
                <Home className="trust-icon" />
                <h3>Local Walsall Service</h3>
                <p>Based in your community, caring for local cats</p>
              </CardContent>
            </Card>

            <Card className="trust-card">
              <CardContent className="trust-content">
                <Heart className="trust-icon" />
                <h3>Consistent Sitter</h3>
                <p>Same familiar face every visit, building trust with your cats</p>
              </CardContent>
            </Card>

            <Card className="trust-card">
              <CardContent className="trust-content">
                <Camera className="trust-icon" />
                <h3>Free Meet & Greet</h3>
                <p>Get to know us before your first booking, no obligation</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Latest from the Blog */}
      <LatestFromBlog />

      {/* Customer Reviews */}
      <CustomerReviews />

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Give Your Cats the Best Care?</h2>
            <p>Book a free meet and greet today. Let's discuss your cats' needs and how we can help.</p>
            <p className="discount-highlight">Get 10% off your first booking with code WALSALL10</p>
            <Link to="/contact">
              <Button size="lg" className="cta-button-large">
                Book Your Free Meet & Greet
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
